import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  isAggregatedFormControl,
  isFormControl,
  isFormGroup,
} from '@fmnts/common/forms';
import { map, share } from 'rxjs';

/**
 * @param group `FormGroup` / `FormArray` which controls should be queries
 * @param predicate Function that is called with the `FormControl` and the controls key
 *
 * @returns
 * All `FormControl`s in the `FormGroup` / `FormArray` (and )
 */
function queryFormControls(
  group: UntypedFormGroup | UntypedFormArray,
  predicate: (control: UntypedFormControl, key: string) => boolean,
): UntypedFormControl[] {
  return Object.entries(group.controls).flatMap(([key, control]) =>
    isAggregatedFormControl(control)
      ? queryFormControls(control, predicate) // Recursively flatten
      : isFormControl(control)
        ? predicate(control, key)
          ? control // Include control on fulfilled predicate
          : []
        : [],
  );
}

/**
 * Flattens the value of a `FormGroup`, so that the result
 * will contain values of enabled, nested `FormControl`s.
 * Only enabled `FormGroups` are considered. Controls in `FormArray`s
 * are ignored.
 *
 * @param group Group thats value should be flattened
 *
 * @returns
 * Flattened value of the given `group`
 *
 * @example
 * const root = new FormGroup({
 *  field1: new FormControl('value-1'),
 *  group2: new FormGroup({
 *    field2: new FormControl('value-2'),
 *    field3: new FormControl('value-3'),
 *  })
 * });
 *
 * // {field1: 'value-1', field2: 'value-2', field3: 'value-3'}
 * flattenFormGroup(root);
 */
function flattenFormGroup(group: UntypedFormGroup): Record<string, unknown> {
  return Object.entries(group.controls).reduce(
    (acc, [key, control]) => {
      if (control.enabled) {
        if (isFormControl(control)) {
          return { ...acc, [key]: control.value as unknown };
        } else if (isFormGroup(control)) {
          return { ...acc, ...flattenFormGroup(control) };
        }
      }

      return acc;
    },
    {} as Record<string, any>,
  );
}

/**
 * Extension for angulars `FormGroup`.
 *
 * It offeres utilities that are used within the `declarative-forms` module
 * by field components and the `declarative-form.component` and is built
 * based on a `FormDeclaration`
 */
export class DeclarativeForm extends UntypedFormGroup {
  /**
   * A multicasting observable that emits the `flatValue` every time the value of
   * the control changes, in the UI or programmatically. It also emits an event each
   * time you call `enable()` or `disable()` without passing along
   * `{emitEvent: false}` as a function argument.
   *
   * @see {@link DeclarativeForm.flatValue}
   */
  public flatValueChanges = this.valueChanges.pipe(
    map(() => flattenFormGroup(this)),
    share(),
  );

  /**
   * Flattened value of all enabled `FormControl`s as an object.
   *
   * @example
   * const root = new DeclarativeForm({
   *  field1: new FormControl('value-1'),
   *  group2: new FormGroup({
   *    field2: new FormControl('value-2'),
   *    field3: new FormControl('value-3'),
   *  }),
   *  group3: new FormGroup({
   *    field4: new FormControl('value-4'),
   *  })
   * });
   *
   * // disable control
   * root.get('group2.field2').disable();
   * // or disable group
   * root.get('group3').disable();
   *
   * root.flatValue(root); // -> {field1: 'value-1', field3: 'value-3'}
   */
  public get flatValue(): Record<string, unknown> {
    return flattenFormGroup(this);
  }

  /**
   * @param key Key of the control
   *
   * @returns
   * All `FormControl`s with the given `key`.
   */
  public getControlsByKey(key: string): UntypedFormControl[] {
    return queryFormControls(this, (_, controlKey) => key === controlKey);
  }
}
